import { box, button, column, image, row, select, styled, text } from '@tiendanube/nube-sdk-ui';
import { formatMoney } from './format.js';
import { applyTierDiscount, hasDiscount, offerProductIdOf, offerVariantSource, pickerDefaultId, shouldSplit, splitLevels, tierBase, tierById, unitVariantIds, visibleOfferTiers, } from './offers.logic.js';
import { bestDealCss, offerCardCss, offerStyles } from './offers.theme.js';
import { outOfStockVariantIds, withSoldOutLabel } from './stock.js';
/** The tier whose card is highlighted right now. */
function selectedTier(o, offer) {
    return tierById(offer, o.build.state.selectedTierId) ?? undefined;
}
/** `{product_name}` is the only token a tier title supports. */
function tierTitle(tier, productName) {
    return tier.title.replace(/\{product_name\}/g, productName);
}
/**
 * The tier thumbnail: an explicit per-tier image, then the live product image, then the image
 * captured for the offer's product in the admin, and only then the product name as text.
 *
 * That last fallback is not decoration — some themes expose no product image at all, which is why
 * the admin captures one when the merchant picks the product.
 */
function thumbNode(tier, o, s, fallbackImage) {
    const src = tier.image_url || o.ctx.product.image || fallbackImage;
    return box({
        key: 'thumb',
        style: s.thumb,
        children: [
            src
                ? image({ src, alt: o.ctx.product.name, style: s.thumbImg })
                : text({ style: s.thumbFallback, children: [o.ctx.product.name] }),
        ],
    });
}
/** The plaque badge ("Save 25%"), or nothing when the tier has no plaque text. */
function plaqueNode(tier, s, template) {
    if (!tier.plaque_text)
        return [];
    const base = template === 'modern' ? s.plaqueModern : template === 'vertical' ? s.plaqueVertical : s.plaque;
    return [
        text({
            key: 'plaque',
            style: { ...base, backgroundColor: tier.plaque_bg_color, color: tier.plaque_text_color },
            children: [tier.plaque_text],
        }),
    ];
}
/**
 * The "Best deal" badge, or nothing. Rendered on the **preselected** tier only — the merchant
 * marks one tier as the deal to take, and `design` holds a single badge for the whole offer, so a
 * tier that is not preselected can never carry one (see the schema note on `best_deal_show`).
 *
 * Animated badges go through `styled()` because `@keyframes` is not expressible in a style object;
 * an un-animated one stays a plain `text`, so the common case adds no CSS at all.
 */
function bestDealNode(tier, design, s) {
    const caption = design.best_deal_text.trim();
    if (!design.best_deal_show || !tier.preselect || !caption)
        return [];
    const style = {
        ...(design.template === 'classic' ? s.bestDeal : s.bestDealStacked),
        backgroundColor: design.best_deal_bg_color,
        color: design.best_deal_text_color,
        borderRadius: `${design.best_deal_border_radius}px`,
    };
    const css = bestDealCss(design);
    if (!css)
        return [text({ key: 'best-deal', style, children: [caption] })];
    const Badge = styled(text) `
    ${css}
  `;
    return [Badge({ key: 'best-deal', style, children: [caption] })];
}
/** The radio marker. `vertical` has none — its whole card is the target. */
function radioNode(selected, s, style) {
    return box({
        key: 'radio',
        style: { ...(selected ? s.radioOn : s.radio), ...(style ?? {}) },
        children: selected ? [box({ style: s.radioDot, children: [] })] : [],
    });
}
/** Struck-through base price (when the tier discounts and the merchant kept it) plus the net. */
function pricesNode(tier, o, s, base, design) {
    const money = (n) => formatMoney(n, o.ctx.currency, o.ctx.locale ?? 'es');
    return row({
        key: 'prices',
        style: design.template === 'vertical'
            ? s.pricesVertical
            : design.template === 'modern'
                ? s.pricesStacked
                : s.prices,
        children: [
            ...(hasDiscount(tier) && !tier.hide_compare_price
                ? [text({ key: 'compare', style: s.compare, children: [money(base)] })]
                : []),
            text({
                key: 'price',
                style: { ...s.price, color: tier.price_color, fontSize: `${design.prices_size}px` },
                children: [money(applyTierDiscount(base, tier))],
            }),
        ],
    });
}
/* ── Per-unit variant pickers ──────────────────────────────────────────────────────────── */
/** One numbered row: either a combined dropdown, or two dependent ones. */
function pickerRow(o, s, variants, optionNames, design, unitIndex, variantId, soldOut) {
    const onPick = o.build.handlers?.onTierVariantChange;
    const numbered = !design.hide_variant_numbers;
    const split = shouldSplit(variants, optionNames);
    // The offer's CAPTURED variants first, then the live product's. A capture taken before the
    // merchant last edited the catalogue can be missing the variant the buyer has selected on the
    // page, and the split row below then falls back to the first option of each level — showing
    // "Negro / S" under a page reading "Negro / XL". The live product knows the values either way.
    const current = variants.find((v) => v.id === variantId) ??
        o.ctx.product.variants?.find((v) => v.id === variantId);
    // A variable, not an inline literal on each `select` — `container` is honoured by the adapter
    // but missing from the select's declared style, and it is what cancels the theme's `form-group`
    // margin under every picker. See `SelectStyle` in `theme.ts`.
    const pickerStyle = {
        container: s.pickerContainer,
        label: s.pickerLabelHidden,
        select: s.picker,
    };
    const selects = [];
    if (split) {
        const { firsts, secondsFor, idFor, firstSoldOut, pairSoldOut } = splitLevels(variants, soldOut);
        // Each level falls back to its own first entry unless the selected variant names a value that
        // level actually offers. Validated rather than trusted: `current` may come from the live
        // product (above), whose catalogue can carry a combination the captured levels do not — and a
        // `select` whose value is absent from its options renders as no selection at all.
        //
        // 🚨 The fallbacks skip SOLD-OUT values too. A `select` opened on a disabled option shows the
        // buyer a sold-out size they cannot change away from, and submits it anyway — greying options
        // out is only half the fix.
        const wantedFirst = current?.values?.[0];
        const firstFallback = firsts.find((v) => !firstSoldOut(v)) ?? firsts[0];
        const first = (wantedFirst && firsts.includes(wantedFirst) ? wantedFirst : firstFallback) ?? '';
        const seconds = secondsFor(first);
        const secondFallback = seconds.find((v) => !pairSoldOut(first, v)) ?? seconds[0];
        const wantedSecond = current?.values?.[1];
        const second = (wantedSecond && seconds.includes(wantedSecond) ? wantedSecond : secondFallback) ?? '';
        const pick = (a, b) => {
            // Resolve the pair back to a concrete variant id before reporting — the caller only ever
            // stores ids, so combined and split rows are indistinguishable upstream.
            const id = idFor(a, b) ?? idFor(a, secondsFor(a)[0] ?? '') ?? variantId;
            onPick?.(unitIndex, id);
        };
        selects.push(select({
            key: 'a',
            name: `__tier_${unitIndex}_a`,
            label: optionNames[0] ?? '',
            value: first,
            options: firsts.map((v) => ({
                label: withSoldOutLabel(v, firstSoldOut(v)),
                value: v,
                disabled: firstSoldOut(v),
            })),
            style: pickerStyle,
            ...(onPick
                ? {
                    onChange: (e) => {
                        const a = String(e.value ?? '');
                        // Re-scope the second level: keep the current value when the new first still
                        // offers it, else fall back to that level's first entry.
                        const next = secondsFor(a);
                        // Keep the current size when the new colour still sells it AND it is in stock;
                        // otherwise land on the first one that is, never on a disabled option.
                        const keep = next.includes(second) && !pairSoldOut(a, second);
                        const nextAvailable = next.find((v) => !pairSoldOut(a, v)) ?? next[0] ?? '';
                        pick(a, keep ? second : nextAvailable);
                    },
                }
                : {}),
        }), select({
            key: 'b',
            name: `__tier_${unitIndex}_b`,
            label: optionNames[1] ?? '',
            value: second,
            options: seconds.map((v) => ({
                label: withSoldOutLabel(v, pairSoldOut(first, v)),
                value: v,
                disabled: pairSoldOut(first, v),
            })),
            style: pickerStyle,
            ...(onPick
                ? {
                    onChange: (e) => pick(first, String(e.value ?? '')),
                }
                : {}),
        }));
    }
    else {
        selects.push(select({
            key: 'combined',
            name: `__tier_${unitIndex}`,
            label: '',
            // Never open on a greyed-out option — see `pickerDefaultId`.
            value: String(pickerDefaultId(variants, variantId, soldOut)),
            options: variants.map((v) => ({
                label: withSoldOutLabel(v.name, soldOut.has(v.id)),
                value: String(v.id),
                disabled: soldOut.has(v.id),
            })),
            style: pickerStyle,
            ...(onPick
                ? {
                    onChange: (e) => onPick(unitIndex, Number(e.value ?? variantId)),
                }
                : {}),
        }));
    }
    return row({
        key: `unit-${unitIndex}`,
        style: s.pickerRow,
        children: [
            ...(numbered
                ? [text({ key: 'n', style: s.pickerNumber, children: [`${unitIndex + 1}.`] })]
                : []),
            // Each picker sits in a box of ours, which is what the row actually lays out — a `select`
            // renders inside a wrapper we cannot style, so without this the dropdowns shrink-wrap their
            // option text and bunch at the left instead of filling the row. See `pickerField`.
            ...selects.map((sel, i) => box({ key: `pick-${i}`, style: s.pickerField, children: [sel] })),
        ],
    });
}
/** The picker box for the selected tier — one row per unit it sells. */
function pickersNode(o, offer, tier, s) {
    const { variants, optionNames, showPickers } = offerVariantSource(offer, o.ctx);
    if (!showPickers)
        return null;
    const ids = unitVariantIds(tier, o.ctx, o.build.state.tierVariantIds);
    // From the LIVE catalogue, while `variants` above are the offer's CAPTURED ones — matched by id,
    // which is what lets a capture taken months ago still grey out what has sold out since.
    const soldOut = outOfStockVariantIds(o.ctx);
    return column({
        key: 'pickers',
        style: offer.design.template === 'modern' ? s.pickersModern : s.pickers,
        children: ids.map((id, i) => pickerRow(o, s, variants, optionNames, offer.design, i, id, soldOut)),
    });
}
/* ── Cards ─────────────────────────────────────────────────────────────────────────────── */
function tierCard(o, offer, tier, selected, s, opts) {
    const design = offer.design;
    // Resolved, never raw: `state.tierVariantIds` is sparse, and its gaps price at the buyer's
    // current product-page variant (see `FormState.tierVariantIds`).
    const base = tierBase(tier, o.ctx, selected ? unitVariantIds(tier, o.ctx, o.build.state.tierVariantIds) : undefined);
    const onSelect = o.build.handlers?.onTierSelect;
    const title = text({
        key: 'title',
        style: s.title,
        children: [tierTitle(tier, o.ctx.product.name)],
    });
    const prices = pricesNode(tier, o, s, base, design);
    const plaque = plaqueNode(tier, s, design.template);
    const bestDeal = bestDealNode(tier, design, s);
    // Classic and modern reveal the pickers INSIDE the selected card; vertical's columns are too
    // narrow for a readable dropdown, so it keeps them full-width below the grid (see offersNode).
    const inline = selected && design.template !== 'vertical' ? pickersNode(o, offer, tier, s) : null;
    let main;
    if (design.template === 'modern') {
        // Modern's plaque is a full-height block on the card's left edge, so "beside the plaque" is not
        // a place a pill can go. The badge joins the body row instead, after the prices.
        const body = column({
            key: 'body',
            style: s.bodyModern,
            children: [box({ key: 'info', style: s.info, children: [title] }), prices, ...bestDeal],
        });
        main = row({
            key: 'main',
            style: s.mainModern,
            children: [...plaque, body, radioNode(selected, s, s.radioModern)],
        });
    }
    else if (design.template === 'vertical') {
        // Vertical's plaque is a full-width footer; the badge sits centred under the prices, inside the
        // body, rather than competing with it for the footer.
        const body = column({
            key: 'body',
            style: s.bodyVertical,
            children: [
                ...(design.hide_product_image ? [] : [thumbNode(tier, o, s, opts.fallbackImage)]),
                box({ key: 'info', style: s.info, children: [title] }),
                prices,
                ...bestDeal,
            ],
        });
        main = column({ key: 'main', style: s.mainVertical, children: [body, ...plaque] });
    }
    else {
        // Classic: radio top-right, and the plaque + badge as one wrapping row beneath it. The row is
        // built even for a lone plaque so the tail's `space-between` keeps behaving identically.
        const badges = plaque.length + bestDeal.length > 0
            ? [row({ key: 'badges', style: s.badges, children: [...plaque, ...bestDeal] })]
            : [];
        main = row({
            key: 'main',
            style: s.main,
            children: [
                ...(design.hide_product_image ? [] : [thumbNode(tier, o, s, opts.fallbackImage)]),
                column({ key: 'info', style: s.info, children: [title, prices] }),
                column({ key: 'tail', style: s.tail, children: [radioNode(selected, s), ...badges] }),
            ],
        });
    }
    // 🚨 The pickers live OUTSIDE the clickable button, not inside it.
    //
    // The legacy card was a `div` whose selects called `stopPropagation` (`isolateSelect`), because
    // a click on a dropdown otherwise bubbles to the card's own selection handler. There is no event
    // object here to stop, so the equivalent is structural: only the card's MAIN ROW is a button,
    // and the pickers are its sibling. Nesting them meant every touch of a dropdown re-fired
    // `onTierSelect`, which discarded the picks the buyer had already made and dropped any applied
    // coupon — the bundle opened as N units of the page variant however carefully it was configured.
    //
    // `styled()` for the theme defence: the style prop's `!important` loses to a theme's own button
    // rules. See `offerCardCss`.
    const Main = styled(button) `
    ${offerCardCss(o.config.appearance.text_color)}
  `;
    const clickable = Main({
        key: 'select',
        // Vertical's button has to grow, or the plaque footer inside it stops at the content instead of
        // the card's bottom edge — see `mainVertical`.
        style: design.template === 'vertical' ? s.cardButtonVertical : s.cardButton,
        ...(onSelect ? { onClick: () => onSelect(tier.id) } : {}),
        children: [main],
    });
    return column({
        // The tier id, so a card keeps its identity when another card's selection state changes the
        // style object beside it. See `identity.ts`.
        key: `tier-${tier.id}`,
        style: {
            ...(selected ? s.cardSelected : s.card),
            ...(design.template === 'vertical' ? s.cardVertical : {}),
        },
        children: inline ? [clickable, inline] : [clickable],
    });
}
/**
 * The offer block. Returns null when the offer has no tiers, so the caller drops it entirely
 * rather than rendering an empty container.
 */
export function offersNode(o, offer, opts = {}) {
    const tiers = visibleOfferTiers(offer);
    if (tiers.length === 0)
        return null;
    const s = offerStyles(offer.design, o.config.appearance.text_color);
    const chosen = selectedTier(o, offer);
    // The offer's captured product image, unless the caller named one.
    const fallbackImage = opts.fallbackImage ?? offer.products.find((p) => p.id === offerProductIdOf(o.ctx))?.image;
    const cardOpts = { ...opts, ...(fallbackImage ? { fallbackImage } : {}) };
    const cards = tiers.map((tier) => tierCard(o, offer, tier, chosen?.id === tier.id, s, cardOpts));
    if (offer.design.template === 'vertical') {
        const shared = chosen ? pickersNode(o, offer, chosen, s) : null;
        return column({
            style: opts.aboveBuyButton ? s.containerAbove : s.container,
            children: [
                row({ key: 'grid', style: s.verticalRow, children: cards }),
                ...(shared ? [shared] : []),
            ],
        });
    }
    return column({
        style: opts.aboveBuyButton ? s.containerAbove : s.container,
        children: cards,
    });
}
//# sourceMappingURL=offers.js.map